import { useContext, useEffect, useState, useCallback } from 'react';
import SecurityIcon from '@mui/icons-material/Security';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PageLayout from '../../../components/layout/PageLayout';
import { usePermisosGestionApi } from '../../../services/api/permisosApi';
import { useMaintenanceApi } from '../../../services/api/maintenanceApi';
import { DarkModeContext } from '../../../context/darkModeContext';
import Swal from 'sweetalert2';

interface Rol {
  Id: number;
  Nombre: string;
  Activo: boolean;
}

interface Modulo {
  Id: number;
  Nombre: string;
  Ruta: string;
  Icono?: string;
  Orden: number;
  Activo: boolean;
}

const Accesos = () => {
  const { darkMode } = useContext(DarkModeContext);
  const { getAllModulos, getModulosByRol, asignarModulosARol, isLoading } = usePermisosGestionApi();
  const { getAll: getRoles } = useMaintenanceApi('roles');

  const [roles, setRoles] = useState<Rol[]>([]);
  const [modulos, setModulos] = useState<Modulo[]>([]);
  const [selectedRolId, setSelectedRolId] = useState<number | null>(null);
  const [modulosAsignados, setModulosAsignados] = useState<Set<number>>(new Set());
  const [modulosOriginales, setModulosOriginales] = useState<Set<number>>(new Set());
  const [loadingRol, setLoadingRol] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadInitial = useCallback(async () => {
    const [rolesData, modulosData] = await Promise.all([
      getRoles(),
      getAllModulos(),
    ]);
    setRoles((rolesData as unknown as Rol[]).filter((r) => r.Activo));
    setModulos((modulosData as Modulo[]).filter((m) => m.Activo).sort((a, b) => a.Orden - b.Orden));
  }, [getRoles, getAllModulos]);

  useEffect(() => {
    loadInitial();
  }, []);

  const handleSelectRol = async (idRol: number) => {
    if (idRol === selectedRolId) return;
    setSelectedRolId(idRol);
    setLoadingRol(true);
    try {
      const asignados = await getModulosByRol(idRol);
      const ids = new Set(asignados.map((m) => m.id));
      setModulosAsignados(new Set(ids));
      setModulosOriginales(new Set(ids));
    } finally {
      setLoadingRol(false);
    }
  };

  const handleToggleModulo = (idModulo: number, checked: boolean) => {
    setModulosAsignados((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(idModulo);
      } else {
        next.delete(idModulo);
      }
      return next;
    });
  };

  const hasChanges =
    selectedRolId !== null &&
    (modulosAsignados.size !== modulosOriginales.size ||
      Array.from(modulosAsignados).some((id) => !modulosOriginales.has(id)));

  const handleSave = async () => {
    if (!selectedRolId) return;
    const result = await Swal.fire({
      title: '¿Guardar cambios?',
      text: 'Se actualizarán los módulos asignados a este rol. Los cambios surten efecto en el próximo inicio de sesión del usuario.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    try {
      await asignarModulosARol(selectedRolId, Array.from(modulosAsignados));
      setModulosOriginales(new Set(modulosAsignados));
    } catch {
      // error already shown by hook
    } finally {
      setSaving(false);
    }
  };

  const selectedRol = roles.find((r) => r.Id === selectedRolId);

  return (
    <PageLayout icon={SecurityIcon} title="Accesos por Rol">
        <Box sx={{ p: 3, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Panel izquierdo: lista de roles */}
          <Card sx={{ width: 240, flexShrink: 0 }}>
            <CardContent sx={{ pb: '8px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AdminPanelSettingsIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Roles
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {isLoading && roles.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List dense disablePadding>
                  {roles.map((rol) => (
                    <ListItemButton
                      key={rol.Id}
                      selected={selectedRolId === rol.Id}
                      onClick={() => handleSelectRol(rol.Id)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText primary={rol.Nombre} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Panel derecho: módulos */}
          <Card sx={{ flex: 1 }}>
            <CardContent>
              {!selectedRolId ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                  <SecurityIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                  <Typography color="text.secondary">
                    Selecciona un rol para ver y editar sus módulos permitidos
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {selectedRol?.Nombre}
                      </Typography>
                      <Chip
                        label={`${modulosAsignados.size} de ${modulos.length} módulos`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Tooltip title={!hasChanges ? 'No hay cambios pendientes' : ''}>
                      <span>
                        <Button
                          variant="contained"
                          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                          onClick={handleSave}
                          disabled={!hasChanges || saving}
                        >
                          Guardar cambios
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {loadingRol ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Marca los módulos a los que este rol tendrá acceso. Los cambios aplican al siguiente inicio de sesión.
                      </Typography>
                      <FormGroup>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 0.5 }}>
                          {modulos.map((modulo) => (
                            <FormControlLabel
                              key={modulo.Id}
                              control={
                                <Checkbox
                                  checked={modulosAsignados.has(modulo.Id)}
                                  onChange={(e) => handleToggleModulo(modulo.Id, e.target.checked)}
                                  size="small"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{modulo.Nombre}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {modulo.Ruta}
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'center', mr: 0 }}
                            />
                          ))}
                        </Box>
                      </FormGroup>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </PageLayout>
  );
};

export default Accesos;
